<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            :validations="validations.unidade"
                            @change="loadFrom('unidade')"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableInfraestrutura"
                            :options="form.infraestruturas"
                            :validations="validations.infraestrutura"
                            @change="loadFrom('infraestrutura')"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableCentroCusto"
                            :options="form.centrosCusto"
                            :validations="validations.centroCusto"
                            @change="loadFrom('centroCusto')"
                            label="Setor"
                            show-default-option
                            text-field="setorNome"
                            v-model="model.centroCustoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableGrupo"
                            :options="form.grupos"
                            @change="loadFrom('grupo')"
                            default-option-text="Todos"
                            label="Grupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.grupoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableSubgrupo"
                            :options="form.subgrupos"
                            @change="loadFrom('subgrupo')"
                            default-option-text="Todos"
                            label="Subgrupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.subgrupoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="disableMaterial"
                            :repository="repositories.relatorioPosicaoEstoque.material"
                            :request-query-string-params="autocompleteQueryStringParams.material"
                            :search-fields="autocompleteFields.material"
                            text-field="descricao"
                            label="Material"
                            uppercase
                            v-model="model.material"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableStatus"
                            :options="form.status"
                            default-option-text="Todos"
                            label="Status"
                            show-default-option
                            v-model="model.status"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
            >
                <template v-slot:thead-top>
                    <rkm-tr>
                        <rkm-th :colspan="fields.length - 4"></rkm-th>

                        <rkm-th
                            class="text-center"
                            colspan="4"
                        >
                            Estoque
                        </rkm-th>
                    </rkm-tr>
                </template>

                <template #cell(codigoMaterial)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(subgrupoNome)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(lote)="{ item }">
                    <div
                        v-for="lote in item.lotes"
                        :key="lote.lote"
                    >
                        {{ lote.lote }}
                    </div>
                </template>

                <template #cell(validadeLote)="{ item }">
                    <div
                        v-for="(lote, index) in item.lotes"
                        :key="index"
                    >
                        {{ lote.validadeLote }}
                    </div>
                </template>

                <template #cell(quantidade)="{ item }">
                    <div
                        v-for="(lote, index) in item.lotes"
                        :key="index"
                    >
                        {{ lote.quantidade }}
                    </div>
                </template>

                <template #cell(status)="{ item }">
                    <div :class="{
                        'text-danger': item.status.value == 'abaixo_minimo',
                        'text-success': item.status.value == 'normal',
                        'text-warning': item.status.value == 'acima_maximo'
                    }">
                        {{ item.status.text }}
                    </div>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { data, error, file, object, repository, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        autocompleteQueryStringParams() {

            const params = this.prepareData()

            delete params.material_id

            return {
                material: { ...params }
            }
        },
        autocompleteFields() {

            return {
                material: [
                    { key: 'codigo_material', searchable: true },
                    { key: 'descricao', searchable: true },
                ],
            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {

            return {
                unidade: { required: true },
                infraestrutura: { required: true },
                centroCusto: { required: true },
            }
        },
        disableInfraestrutura() {

            return this.model.unidadeId === null
        },
        disableCentroCusto() {

            return this.model.infraestruturaId === null
        },
        disableGrupo() {

            return this.model.centroCustoId === null
        },
        disableSubgrupo() {

            return this.model.grupoId === null
        },
        disableMaterial() {

            return this.model.centroCustoId === null
        },
        disableStatus() {

            return this.model.centroCustoId === null
        },
    },
    created() {

        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.almoxarifado'),
            impressao: this.getConectaSusRepository('impressao.almoxarifado'),
            relatorioPosicaoEstoque: this.getConectaSusRepository('almoxarifado.relatorioPosicaoEstoque'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: false,
            indeterminate: true,
            selectedCols: [
                'grupo',
            ],
            columns: [
                { key: 'materialCodigo', label: 'Código do material', sortable: true, class: 'col-min text-center' },
                { key: 'materialDescricao', label: 'Material', sortable: true, fixed: true },
                { key: 'programa', label: 'Programa', sortable: true, fixed: false },
                { key: 'grupoNome', label: 'Grupo', sortable: true, },
                { key: 'subgrupoNome', label: 'Subgrupo', sortable: true, },
                { key: 'lote', label: 'Lote', class: 'col-min text-center', fixed: true },
                { key: 'validadeLote', label: 'Validade', class: 'col-min text-center', fixed: true },
                { key: 'quantidade', label: 'Quantidade', class: 'col-min text-center', fixed: true },
                { key: 'estoqueMinimo', label: 'Minimo', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'estoqueMaximo', label: 'Máximo', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'estoqueDisponivel', label: 'Disponível', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'status', label: 'Status', class: 'col-min text-center', sortable: true, fixed: true },
            ],
            itens: [],
            sortBy: 'materialDescricao',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioPosicaoEstoque.list()
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioPosicaoEstoque.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field != 'material') {

                field = `${field}Id`
            }

            const fields = [
                'unidadeId',
                'infraestruturaId',
                'centroCustoId',
                'grupoId',
                'subgrupoId',
                'material',
            ]

            fields.slice(fields.indexOf(field) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            this.repositories.relatorioPosicaoEstoque.index(this.prepareData())
                .then(({ data }) => this.itens = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            const data = this.prepareDataWithColumns();

            data.sortBy = this.sortBy
            data.sortDesc = this.sortDesc

            this.repositories.impressao.relatorioPosicaoEstoque(data)
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioPosicaoEstoque(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.createIdKeysToObject(data, ['material'])

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [data, error, file, object, repository, stringCase],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    },
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
</style>